import React, { useEffect, useRef, useState } from 'react'
import './shopping-cart.styles.scss';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { useDispatch, useSelector } from 'react-redux';
import CartItemCard from './store-components/cart-item-card.component';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import { useLocation, useNavigate } from 'react-router-dom';
import { setShoppingCart } from '@/state/slices/userSlice';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import FullPageLoader from '@/components/loader/full-page-loader.component';
import { UseCloudGameTransactions } from '@/utils/firebase.utils';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import CartContributionCard from './store-components/cart-contribution-card.component';
import { checkElementAccess } from '@/utils/checkElementAccess';
import useAnalytics from '@/utils/useAnalytics';

const ShoppingCart = ({ 
    gameData, 
    playerData, 
    gameElements, 
    gameStats,
    teamMembers,
    unadjustedPlayerData
}) => {

    const screenSize = useSelector(state => state.contentData.screenSize);
    const shoppingCart = useSelector(state => state.userData.shoppingCart);
    const [ saleList, setSaleList ] = useState([]);
    const [ totalCost, setTotalCost ] = useState(0);
    const [ confirmMessage, setConfirmMessage ] = useState(null);
    const [ alertMessage, setAlertMessage ] = useState(null);
    const [ loading, setLoading ] = useState(false);
    const { trackEvent, trackException } = useAnalytics();
    const approveRef = useRef();
    const location = useLocation();
    let accessInterval;
    const navigate = useNavigate();
    const dispatch = useDispatch();

    useEffect(() => {
        if (!gameData?.gameName) return;
        changeDocumentTitle(`Shopping Cart | ${gameData?.gameName}`);
    }, [gameData?.gameName]);

    useEffect(() => {
        document.getElementsByClassName('game-content')[0].scroll({'top': 0, 'behavior': 'smooth'});
        
        return () => {
            if (accessInterval) clearInterval(accessInterval);
        }
    }, [])

    useEffect(() => {
        if (!shoppingCart) return;
        const price = Object.values(shoppingCart)
        .reduce((acc, cur) => acc + (cur.quantity * cur.cost), 0)
        setTotalCost(price);
    }, [shoppingCart])

    useEffect(() => {
        if (!gameElements || !playerData || !shoppingCart) return;
        checkCartForAccess();
        accessInterval = setInterval(() => {
            checkCartForAccess();
        }, 10000)
    }, [gameElements, playerData, shoppingCart])

    function checkCartForAccess() {
        // console.log(shoppingCart);
        const elementObj = {};
        gameElements.forEach(element => {
            elementObj[element.id] = element;
        });
        for (let itemId of Object.keys(shoppingCart)) {
            const access = checkElementAccess({
                'playerData': playerData,
                'gameElements': elementObj,
                'elementId': itemId,
                'teamMembers': teamMembers
            })
            if (!access || !access.access) {
                let tempCart = {...shoppingCart};
                delete tempCart[itemId]
                dispatch(setShoppingCart({...tempCart}))
            }
        }
    }

    useEffect(() => {
        if (!gameElements) return;
        getSales();
    }, [gameElements])

    function getSales() {
        const now = new Date().getTime();
        setSaleList(
            Object.values(gameElements)
            .filter(e => 
                e.type === 'sale' &&
                e.status === 'active' &&
                (!e.opt_dateOpen || e.opt_dateOpen < now) &&
                (!e.opt_dateClose || e.opt_dateClose > now)
            )
        )
    }

    function changeQuantity(quantity, itemId) {
        // console.log(quantity);
        if (quantity > 0) {
            let tempData = {...shoppingCart};
            let tempItem = {...tempData[itemId]};
            tempItem.quantity = quantity;
            dispatch(setShoppingCart({...tempData, ...{[itemId]: tempItem}}));
            trackEvent(
                'store_item_quantity_changed_in_cart',
                'Player',
                `Player changed quantity for item ${itemId} to ${quantity} in game ${gameData.gameId}`,
                1,
                null
            )
            return;
        }
    }

    function changeContribution(contribution, itemId) {
        let item = gameElements[itemId];
        if (contribution < 1) contribution = 1;
        if (item.opt_contributionMin && contribution < item.opt_contributionMin) {
            contribution = item.opt_contributionMin;
        }
        if (item.opt_contributionMax && contribution > item.opt_contributionMax) {
            contribution = item.opt_contributionMax;
        }
        if (contribution >= 1) {
            let tempData = {...shoppingCart};
            let tempItem = {...tempData[itemId]};
            tempItem.cost = contribution;
            dispatch(setShoppingCart({...tempData, ...{[itemId]: tempItem}}));
            trackEvent(
                'store_contribution_quantity_changed_in_cart',
                'Player',
                `Player changed contribution for item ${itemId} to ${contribution} in game ${gameData.gameId}`,
                1,
                null
            )
            return;
        }
        
    }

    function removeItem(itemId) {
        console.log(itemId)
        let tempData = {...shoppingCart};
        delete tempData[itemId];
        dispatch(setShoppingCart({...tempData}))
        trackEvent(
            'store_item_removed_from_cart',
            'Player',
            `Player removed item ${itemId} from cart in game ${gameData.gameId}`,
            1,
            null
        )
    }

    function confirmPurchase() {
        if (location.pathname.includes('/preview/player/')) {
            setAlertMessage('This is a preview of how the purchase confirmation process ' + 
                'will look for players. In the live game, once you click "Complete Purchase", ' + 
                'your purchase will be processed and you will receive any items, rewards, or ' +
                'contributions you bought in your account. If there are any issues with ' + 
                'processing your purchase, you will receive an alert message with more details.');
            return;
        }
        const unadjustedPlayerCurrency = unadjustedPlayerData.currency ?? 0;
        if (totalCost > unadjustedPlayerCurrency) {
            setAlertMessage(`Sorry! You don't have enough coins to complete this purchase. Check your Rewards to see if your coin balance has changed recently.`);
            return;
        }
        setConfirmMessage(`<h3>Are you sure you want to make this purchase?</h3><p>You will be spending <b>${totalCost.toLocaleString()}</b> coins which will leave you with <b>${(unadjustedPlayerCurrency - totalCost).toLocaleString()}</b> coins.</p>`)
        approveRef.current = () => {
            continuePurchase();
        }

        async function continuePurchase() {
            setLoading(true);
            const res = await UseCloudGameTransactions(
                'processShoppingCartPurchase', 
                {
                    'gameId': gameData.gameId,
                    'playerId': playerData.playerId,
                    'cart': shoppingCart
                }
            )
            if (res.error) {
                setAlertMessage(`<p>Sorry, but your purchase couldn't go through. Here was the error message:</p><p>"${res.error}"</p>`);
                setLoading(false);
                trackException(`Shopping Cart Purchase Error: ${res.error}`);
                return;
            }
            console.log(res);
            setAlertMessage('<h3>Purchase completed successfully!</h3><br /><br />If you purchased a prize pack, it may take a minute or so to populate in your Rewards page.');
            dispatch(setShoppingCart({}));
            setLoading(false);
            trackEvent(
                'shopping_cart_purchase_completed',
                'Player',
                `Player ${playerData.appId} completed a shopping cart purchase in game ${gameData.gameId}`,
                totalCost,
                null
            );
            return;
        }
    }

    return (
        <div className='shopping-cart'>
            <div className='g-card cart-card'>
                <BackButton cancel={() => navigate(-1)} />
                <div className="g-space-1"></div>
                <div className='player-title'>
                    <ImageContainer src='/images/icons/shopping-cart.png' alt='marketplace icon' className='player-title-icon' />
                    My Shopping Cart
                </div>
                <hr />
                <section className='current-balance'>
                    <span>Current Balance: </span>
                    <ImageContainer src="/images/icons/coin.png" className='balance-coin' />
                    <span>{playerData.currency ? playerData.currency.toLocaleString() : 0}</span>
                </section>
                <div className="g-space-1"></div>
                <div className='cart-item-list'>
                    {
                        (shoppingCart && gameElements) &&
                        Object.values(shoppingCart).map(i => {
                            const element = gameElements.find(e => e.id === i.id);

                            return (
                            <div key={i.id}>
                                {
                                    (element && element.opt_itemType === 'contribution')
                                    ?
                                    <CartContributionCard 
                                        cartItem={i} 
                                        itemData={element}
                                        changeContribution={changeContribution}
                                        removeItem={removeItem} 
                                    />
                                    :
                                    <CartItemCard 
                                        cartItem={i} 
                                        itemData={element}
                                        changeQuantity={changeQuantity}
                                        removeItem={removeItem} 
                                        saleList={saleList}
                                        gameElements={gameElements}
                                    />
                                }
                            </div>
                        )})
                    }
                </div>
                <div className="g-space-2"></div>
                <div className="buttons">
                    <div className='total'>
                        Purchase Total: 
                        <ImageContainer src="/images/icons/coin.png" className='coin-icon' />
                        {totalCost.toLocaleString()}
                    </div>
                    {
                        (loading)
                        ?
                        <button className='g-button'><ThreeDotsLoader /></button>
                        :
                        <button 
                            className={`g-button ${
                                Object.values(shoppingCart).length > 0 &&
                                playerData.currency >= totalCost
                                    ? 'primary' 
                                    : 'disabled'
                            } ${
                                screenSize === 'mobile' ? 'med-btn' : ''
                            }`}
                            onClick={() => confirmPurchase()}
                        >Complete Purchase</button>
                    }
                </div>
            </div>
            <ModalAlert
                show={alertMessage}
                cancel={() => setAlertMessage(null)}
                message={alertMessage}     
            />
            <ModalConfirm
                show={confirmMessage}
                cancel={() => setConfirmMessage(null)}
                message={confirmMessage}
                onApprove={approveRef.current}
                buttonText='Complete Purchase'
                buttonClass='primary'
            />
            <FullPageLoader show={loading} />
        </div>
    )
}

export default ShoppingCart